// Wareneingang — Versand-Workflow-Seite
// 3 Sektionen: Überfällig (rot) · Erwartet · Heute eingegangen
import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Package, Check, AlertTriangle, Clock, PackageCheck, RefreshCw } from 'lucide-react';
import { api, type Beistelteil } from '../lib/api';

type Liste = {
  ueberfaellig: Beistelteil[];
  erwartet: Beistelteil[];
  heute_eingegangen: Beistelteil[];
};

export default function Wareneingang() {
  const [data, setData] = useState<Liste | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [eingangFor, setEingangFor] = useState<Beistelteil | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.wareneingang.liste();
      setData(res.data);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Fehler beim Laden');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Auto-Refresh alle 15 Sek
  useEffect(() => {
    const id = setInterval(load, 15_000);
    return () => clearInterval(id);
  }, [load]);

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Package className="w-6 h-6 text-blue-600" />
          Wareneingang
        </h1>
        <button
          onClick={load}
          className="inline-flex items-center gap-1 px-3 py-1.5 text-sm border border-slate-300 rounded hover:bg-slate-50"
        >
          <RefreshCw className="w-3.5 h-3.5" /> Aktualisieren
        </button>
      </div>

      {loading && !data && (
        <div className="space-y-4">
          {[1, 2, 3].map(i => <div key={i} className="h-32 bg-slate-100 rounded animate-pulse" />)}
        </div>
      )}

      {error && (
        <div className="bg-red-50 text-red-700 border border-red-200 p-3 rounded mb-4">
          {error} <button onClick={load} className="underline ml-2">Retry</button>
        </div>
      )}

      {data && (
        <>
          <Section
            title="Überfällig"
            icon={<AlertTriangle className="w-5 h-5 text-red-600" />}
            count={data.ueberfaellig.length}
            color="red"
            items={data.ueberfaellig}
            onEingang={setEingangFor}
            ueberfaellig
          />
          <Section
            title="Erwartet"
            icon={<Clock className="w-5 h-5 text-amber-600" />}
            count={data.erwartet.length}
            color="amber"
            items={data.erwartet}
            onEingang={setEingangFor}
          />
          <Section
            title="Heute eingegangen"
            icon={<PackageCheck className="w-5 h-5 text-emerald-600" />}
            count={data.heute_eingegangen.length}
            color="emerald"
            items={data.heute_eingegangen}
            onEingang={null}
          />
        </>
      )}

      {eingangFor && (
        <EingangModalInline
          item={eingangFor}
          onClose={() => setEingangFor(null)}
          onSaved={() => { setEingangFor(null); load(); }}
        />
      )}
    </div>
  );
}

type SectionColor = 'red' | 'amber' | 'emerald';
const COLOR_CLASS: Record<SectionColor, string> = {
  red:     'border-red-200 bg-red-50',
  amber:   'border-amber-200 bg-amber-50',
  emerald: 'border-emerald-200 bg-emerald-50',
};

function Section({
  title, icon, count, color, items, onEingang, ueberfaellig
}: {
  title: string; icon: React.ReactNode; count: number; color: SectionColor;
  items: Beistelteil[]; onEingang: ((b: Beistelteil) => void) | null;
  ueberfaellig?: boolean;
}) {
  if (count === 0 && !ueberfaellig) return null;
  return (
    <div className={`border rounded-xl ${COLOR_CLASS[color]} p-4 mb-4`}>
      <div className="flex items-center gap-2 mb-3">
        {icon}
        <h2 className="font-semibold">{title}</h2>
        <span className="text-slate-500 text-sm">({count})</span>
      </div>
      {items.length === 0 ? (
        <div className="text-slate-500 text-sm py-2">Keine Einträge</div>
      ) : (
        <div className="bg-white rounded-lg border border-slate-200 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs text-slate-600 uppercase">
              <tr>
                <th className="text-left px-3 py-2">Bezeichnung</th>
                <th className="text-left px-3 py-2">Auftrag</th>
                <th className="text-left px-3 py-2">{ueberfaellig ? 'Seit' : 'Erwartet'}</th>
                <th className="text-left px-3 py-2">Stk</th>
                <th className="text-left px-3 py-2">Lagerplatz</th>
                {onEingang && <th className="px-3 py-2 w-24"></th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {items.map(item => (
                <tr key={item.id} className="hover:bg-slate-50">
                  <td className="px-3 py-2 font-medium">{item.bezeichnung}</td>
                  <td className="px-3 py-2">
                    <Link
                      to={`/${item.auftrag_modul}/${item.auftrag_id}`}
                      className="text-blue-600 hover:underline"
                    >
                      #{item.auftrag_id} ({moduleLabel(item.auftrag_modul)})
                    </Link>
                  </td>
                  <td className="px-3 py-2 text-slate-600">
                    {item.erwartet_bis ? formatDate(item.erwartet_bis) : '—'}
                  </td>
                  <td className="px-3 py-2">{item.erwartete_stueckzahl}</td>
                  <td className="px-3 py-2 text-slate-600">{item.lagerplatz ?? '—'}</td>
                  {onEingang && (
                    <td className="px-3 py-2">
                      <button
                        onClick={() => onEingang(item)}
                        className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-emerald-600 text-white rounded hover:bg-emerald-700"
                      >
                        <Check className="w-3 h-3" /> Eingang
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function moduleLabel(m: string): string {
  return ({
    drueckteile: 'Drückteile',
    laufraeder: 'Laufräder',
    baugruppen: 'Baugruppen',
    montage: 'Montage'
  } as Record<string, string>)[m] ?? m;
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  return `${String(d.getDate()).padStart(2, '0')}.${String(d.getMonth()+1).padStart(2, '0')}.${d.getFullYear()}`;
}

// Minimaler Eingang-Modal direkt in dieser Datei (vermeidet zirkulären Import)
function EingangModalInline({ item, onClose, onSaved }: {
  item: Beistelteil; onClose: () => void; onSaved: () => void;
}) {
  const today = new Date().toISOString().slice(0, 10);
  const [eingangDatum, setEingangDatum] = useState(today);
  const [istStueck, setIstStueck] = useState(item.erwartete_stueckzahl);
  const [lagerplatz, setLagerplatz] = useState('');
  const [zustand, setZustand] = useState<'ok'|'beschaedigt'|'unklar'>('ok');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      await api.beistelteile.update(item.id, {
        status: zustand === 'beschaedigt' ? 'beschaedigt' : 'eingegangen',
        eingang_datum: eingangDatum,
        ist_stueckzahl: istStueck,
        lagerplatz: lagerplatz.trim() || null,
        zustand,
        notes: notes.trim() ? (item.notes ? `${item.notes}\n[Eingang] ${notes.trim()}` : notes.trim()) : item.notes,
      });
      onSaved();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Fehler beim Buchen');
    } finally {
      setSaving(false);
    }
  };

  const teilmenge = istStueck < item.erwartete_stueckzahl;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-md w-full p-5 shadow-2xl">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold">Eingang buchen</h2>
          <button onClick={onClose} className="text-2xl leading-none">×</button>
        </div>
        <div className="bg-slate-50 px-3 py-2 rounded mb-4 text-sm">
          <div className="font-medium">{item.bezeichnung}</div>
          <div className="text-slate-500 text-xs">
            Auftrag #{item.auftrag_id} ({moduleLabel(item.auftrag_modul)}) · Erwartet: {item.erwartete_stueckzahl} Stk
          </div>
        </div>
        <form onSubmit={handleSubmit} className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium mb-1">Eingang am *</label>
              <input
                type="date"
                value={eingangDatum}
                onChange={e => setEingangDatum(e.target.value)}
                max={today}
                className="w-full px-3 py-2 border border-slate-300 rounded text-sm focus:border-blue-500 focus:outline-none"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Stück (Ist) *</label>
              <input
                type="number"
                min={1}
                value={istStueck}
                onChange={e => setIstStueck(parseInt(e.target.value) || 1)}
                className="w-full px-3 py-2 border border-slate-300 rounded text-sm focus:border-blue-500 focus:outline-none"
              />
            </div>
          </div>
          {teilmenge && (
            <div className="bg-amber-50 text-amber-800 border border-amber-200 p-2 rounded text-xs">
              ⚠ Teilmenge: {item.erwartete_stueckzahl - istStueck} Stk fehlen
            </div>
          )}
          <div>
            <label className="block text-sm font-medium mb-1">Lagerplatz</label>
            <input
              type="text"
              value={lagerplatz}
              onChange={e => setLagerplatz(e.target.value)}
              className="w-full px-3 py-2 border border-slate-300 rounded text-sm focus:border-blue-500 focus:outline-none"
              placeholder="z.B. Regal A3"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Zustand</label>
            <select
              value={zustand}
              onChange={e => setZustand(e.target.value as 'ok'|'beschaedigt'|'unklar')}
              className="w-full px-3 py-2 border border-slate-300 rounded text-sm focus:border-blue-500 focus:outline-none"
            >
              <option value="ok">OK</option>
              <option value="beschaedigt">Beschädigt</option>
              <option value="unklar">Unklar</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Bemerkung</label>
            <textarea
              value={notes}
              onChange={e => setNotes(e.target.value)}
              rows={2}
              className="w-full px-3 py-2 border border-slate-300 rounded text-sm focus:border-blue-500 focus:outline-none"
            />
          </div>
          {error && (
            <div className="bg-red-50 text-red-700 border border-red-200 p-2 rounded text-sm">{error}</div>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="px-3 py-2 text-sm border border-slate-300 rounded hover:bg-slate-50">
              Abbrechen
            </button>
            <button
              type="submit"
              disabled={saving}
              className="inline-flex items-center gap-1 px-3 py-2 text-sm bg-emerald-600 text-white rounded hover:bg-emerald-700 disabled:opacity-50"
            >
              <Check className="w-4 h-4" /> {saving ? 'Buche…' : 'Eingang buchen'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
